import React, { useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Link } from 'react-router-dom';
import teams from './data/teams';
import TicketList from './components/TicketList';
import { useCombobox } from './hooks/useCombobox';
import { useTeamEventCounts } from './hooks/useTeamEventCounts';
import './App.css';

const SPORT_GLYPH = {
  NBA: '🏀', NFL: '🏈', MLB: '⚾', NHL: '🏒', MLS: '⚽',
};

const SPORT_DESCRIPTOR = {
  NBA: 'Basketball — 30 teams across the league',
  NFL: 'Football — Sunday rituals, coast to coast',
  MLB: 'Baseball — 162 chances per season',
  NHL: 'Hockey — ice cold, fast as ever',
  MLS: 'Soccer — North America\'s rising league',
};

const CustomNavbar = () => (
  <nav className="sticky top-0 z-30 backdrop-blur-md bg-cream/85 border-b border-line">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="flex items-center justify-between h-[68px]">
        <Link to="/" className="flex items-center group" aria-label="FanTra home">
          <img
            src="/fantra_logo.png"
            alt="FanTra"
            className="h-10 w-auto group-hover:opacity-90 transition-opacity"
          />
        </Link>
        <div className="flex items-center gap-1">
          <Link to="/" className="btn-ghost">Home</Link>
        </div>
      </div>
    </div>
  </nav>
);

const App = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sportFilter, setSportFilter] = useState('ALL');

  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = sportFilter === 'ALL' || team.sport === sportFilter;
    return matchesSearch && matchesSport;
  });

  return (
    <Router>
      <div className="min-h-screen">
        <CustomNavbar />
        <Routes>
          <Route path="/" element={
            <HomePage
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              sportFilter={sportFilter}
              setSportFilter={setSportFilter}
              filteredTeams={filteredTeams}
            />
          } />
          <Route path="/tickets/:teamId" element={<TicketList />} />
        </Routes>
        <SiteFooter />
      </div>
    </Router>
  );
};

const SiteFooter = () => (
  <footer className="mt-12 border-t border-line">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-2">
      <p className="text-xs text-ink-3">
        <span className="display-italic mr-1">FanTra</span>
        — see the real cost of game day.
      </p>
      <p className="text-xs text-ink-4 tracking-wide">
        Tickets via SeatGeek &amp; Ticketmaster · Flights via Kiwi · Booking via Expedia
      </p>
    </div>
  </footer>
);

const HomePage = ({ searchTerm, setSearchTerm, sportFilter, setSportFilter, filteredTeams }) => {
  const navigate = useNavigate();
  const { lookup, loaded: countsLoaded } = useTeamEventCounts();
  const [hideEmpty, setHideEmpty] = useState(true);

  // Annotate every team with its live event count, then sort: with-games first,
  // by descending count, then alphabetically. Empty teams sink to the bottom.
  const annotatedTeams = useMemo(() => {
    return filteredTeams
      .map(t => ({ ...t, eventCount: lookup(t.name) }))
      .sort((a, b) => {
        const aHas = (a.eventCount ?? 0) > 0 ? 1 : 0;
        const bHas = (b.eventCount ?? 0) > 0 ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
        if (b.eventCount !== a.eventCount) return (b.eventCount || 0) - (a.eventCount || 0);
        return a.name.localeCompare(b.name);
      });
  }, [filteredTeams, lookup]);

  const visibleTeams = hideEmpty
    ? annotatedTeams.filter(t => (t.eventCount ?? 0) > 0 || !countsLoaded)
    : annotatedTeams;

  const hiddenCount = annotatedTeams.length - visibleTeams.length;

  const suggestions = useMemo(() => {
    if (!searchTerm) return [];
    return teams
      .filter(team => {
        const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesSport = sportFilter === 'ALL' || team.sport === sportFilter;
        return matchesSearch && matchesSport;
      })
      .slice(0, 5);
  }, [searchTerm, sportFilter]);

  const handleSuggestionClick = (team) => {
    setSearchTerm(team.name);
    navigate(`/tickets/${team.id}`);
  };

  const { open, setOpen, activeIndex, containerRef, handleKeyDown } = useCombobox(
    suggestions,
    handleSuggestionClick
  );

  const showSuggestions = open && !!searchTerm;
  const listboxId = 'team-search-listbox';

  return (
    <main>
      {/* ───────────────────────────  HERO  ─────────────────────────── */}
      <section className="hero-bg grain border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-10 pb-12 md:pt-16 md:pb-20 relative">
          <div className="max-w-3xl animate-rise">
            <p className="eyebrow mb-3">Sports trip planner · Tickets + flights, all-in</p>
            <h1 className="display text-[2.2rem] sm:text-5xl md:text-[4.2rem] leading-[1.02] mb-4">
              See the real cost
              <br />
              of <span className="display-italic">game day.</span>
            </h1>
            <p className="text-base md:text-lg text-ink-2 max-w-xl leading-snug">
              Find any upcoming game, add your home city — we'll show the cheapest
              ticket plus round-trip flight as one honest number.
            </p>
          </div>

          {/* Floating "live" search card overlapping the hero */}
          <div className="relative mt-6 md:mt-10 max-w-3xl animate-rise delay-2">
            <div className="card p-2 md:p-2.5 shadow-loft border-line-strong">
              <div className="relative" ref={containerRef}>
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
                  </svg>
                </div>
                <input
                  type="text"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showSuggestions}
                  aria-controls={listboxId}
                  aria-activedescendant={activeIndex >= 0 ? `team-option-${activeIndex}` : undefined}
                  autoComplete="off"
                  placeholder="Search a team — Lakers, Yankees, Maple Leafs…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setOpen(true); }}
                  onFocus={() => setOpen(true)}
                  onKeyDown={handleKeyDown}
                  className="input-field pl-11 pr-4 text-base md:text-lg border-0 bg-transparent shadow-none focus:shadow-none"
                  style={{ borderColor: 'transparent' }}
                />
                {showSuggestions && (
                  <ul
                    id={listboxId}
                    role="listbox"
                    className="absolute left-0 right-0 top-full mt-3 z-30 bg-paper rounded-2xl shadow-loft border border-line list-none p-1.5 overflow-hidden"
                  >
                    {suggestions.map((team, idx) => (
                      <li
                        key={team.id}
                        id={`team-option-${idx}`}
                        role="option"
                        aria-selected={idx === activeIndex}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSuggestionClick(team)}
                        className={`px-4 py-3 cursor-pointer rounded-xl transition-colors flex items-center justify-between gap-3 ${
                          idx === activeIndex ? 'bg-vellum' : 'hover:bg-vellum/60'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg" aria-hidden="true">{SPORT_GLYPH[team.sport] || '·'}</span>
                          <div>
                            <div className="font-medium text-ink">{team.name}</div>
                            <div className="text-xs text-ink-3 tracking-wide">{team.sport}</div>
                          </div>
                        </div>
                        <span className="text-ink-3 text-sm">→</span>
                      </li>
                    ))}
                    {suggestions.length === 0 && (
                      <li className="px-4 py-3 text-ink-3" role="presentation">No teams match — try another spelling.</li>
                    )}
                  </ul>
                )}
              </div>
            </div>

            {/* Sport filter pills under the search */}
            <div className="flex flex-wrap gap-2 justify-start mt-5 md:mt-6">
              {['ALL', 'NBA', 'NFL', 'MLB', 'NHL', 'MLS'].map((sport) => (
                <button
                  key={sport}
                  onClick={() => setSportFilter(sport)}
                  className={`pill ${sportFilter === sport ? 'pill-active' : ''}`}
                  aria-pressed={sportFilter === sport}
                >
                  {sport !== 'ALL' && <span aria-hidden="true">{SPORT_GLYPH[sport]}</span>}
                  {sport === 'ALL' ? 'All sports' : sport}
                </button>
              ))}
            </div>
          </div>

          {/* Decorative serif quote-like flourish in the corner */}
          <div className="hidden lg:block absolute right-12 top-24 text-terracotta/30 select-none pointer-events-none animate-fade delay-3">
            <span className="serif-flourish text-[10rem] leading-none">✻</span>
          </div>
        </div>
      </section>

      {/* ───────────────────────────  RESULTS / DEFAULT  ─────────────────────────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 md:py-14">
        {(searchTerm || sportFilter !== 'ALL') ? (
          <>
            <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
              <div>
                <p className="eyebrow mb-1">Teams</p>
                <h2 className="display text-2xl md:text-3xl">
                  {visibleTeams.length} {visibleTeams.length === 1 ? 'match' : 'matches'}
                  {sportFilter !== 'ALL' && <span className="text-ink-3"> · {sportFilter}</span>}
                  {searchTerm && <span className="text-ink-3"> · "{searchTerm}"</span>}
                </h2>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {countsLoaded && hiddenCount > 0 && (
                  <button
                    onClick={() => setHideEmpty(v => !v)}
                    className="btn-ghost text-sm"
                  >
                    {hideEmpty ? `Show ${hiddenCount} without games` : 'Hide teams without games'}
                  </button>
                )}
                {(searchTerm || sportFilter !== 'ALL') && (
                  <button
                    onClick={() => { setSearchTerm(''); setSportFilter('ALL'); }}
                    className="btn-ghost text-sm"
                  >
                    ← Clear filters
                  </button>
                )}
              </div>
            </div>

            {visibleTeams.length === 0 ? (
              <div className="text-center py-12 max-w-md mx-auto">
                <p className="display text-xl text-ink-2 mb-1">Nothing here yet.</p>
                <p className="text-sm text-ink-3">
                  {countsLoaded && annotatedTeams.length > 0
                    ? 'No teams in this sport have upcoming games.'
                    : 'Try a different sport filter or check the spelling.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {visibleTeams.map((team, idx) => (
                  <TeamCard
                    key={team.id}
                    team={team}
                    countsLoaded={countsLoaded}
                    onClick={() => navigate(`/tickets/${team.id}`)}
                    delay={Math.min(idx * 25, 250)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <PopularSports onPick={(sport) => { setSearchTerm(''); setSportFilter(sport); }} />
        )}
      </section>

      {/* ───────────────────────────  HOW IT WORKS  ─────────────────────────── */}
      <section className="border-t border-line bg-vellum/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-2xl mb-7">
            <p className="eyebrow mb-2">How it works</p>
            <h2 className="display text-3xl md:text-4xl leading-[1.05]">
              Three honest numbers, <span className="display-italic">one decision.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { n: '01', t: 'Pick a team', b: '130+ teams across NBA, NFL, MLB, NHL, and MLS.' },
              { n: '02', t: 'Drop your city', b: 'Round-trip flights aligned to each game\'s tip-off.' },
              { n: '03', t: 'See the real cost', b: 'Cheapest ticket + flight = all-in price per game.' },
            ].map((step) => (
              <div key={step.n} className="card p-5">
                <p className="num text-3xl text-terracotta mb-2">{step.n}</p>
                <h3 className="display text-lg mb-1">{step.t}</h3>
                <p className="text-sm text-ink-2 leading-snug">{step.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

// Single team card. `team.eventCount` is null until the backend cache returns,
// then `0` (no upcoming) or a positive number (has games).
const TeamCard = ({ team, countsLoaded, onClick, delay }) => {
  const hasGames = (team.eventCount ?? 0) > 0;
  const isEmpty = countsLoaded && team.eventCount === 0;

  return (
    <button
      onClick={onClick}
      className={`card card-lift p-4 text-left group reveal ${isEmpty ? 'opacity-60' : ''}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-2xl text-ink-2 group-hover:text-terracotta transition-colors" aria-hidden="true">
          {SPORT_GLYPH[team.sport] || '·'}
        </span>
        <span className="eyebrow text-[0.6rem]">{team.sport}</span>
      </div>
      <h3 className="display text-base mb-0.5 leading-snug">{team.name}</h3>
      {team.venue && <p className="text-xs text-ink-3 mb-3">{team.venue}</p>}
      <div className="flex items-center justify-between pt-2.5 border-t border-line">
        {!countsLoaded ? (
          <span className="text-xs text-ink-3">Checking schedule…</span>
        ) : hasGames ? (
          <span className="text-xs text-ink-2">
            <span className="text-leaf font-medium">{team.eventCount}</span> upcoming
            {team.eventCount === 1 ? ' game' : ' games'}
          </span>
        ) : (
          <span className="text-xs text-ink-3">No upcoming games</span>
        )}
        <span className={`transition-transform ${hasGames ? 'text-terracotta group-hover:translate-x-0.5' : 'text-ink-4'}`}>→</span>
      </div>
    </button>
  );
};

const PopularSports = ({ onPick }) => (
  <>
    <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
      <div>
        <p className="eyebrow mb-1">Browse by sport</p>
        <h2 className="display text-2xl md:text-3xl">Five leagues, every game.</h2>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {['NBA', 'NFL', 'MLB', 'NHL', 'MLS'].map((sport, idx) => (
        <button
          key={sport}
          onClick={() => onPick(sport)}
          className="card card-lift p-5 text-left group reveal"
          style={{ animationDelay: `${idx * 50}ms` }}
        >
          <div className="flex items-start justify-between mb-3">
            <span className="text-3xl text-ink-2 group-hover:text-terracotta transition-colors" aria-hidden="true">
              {SPORT_GLYPH[sport]}
            </span>
            <span className="eyebrow text-[0.6rem]">{sport}</span>
          </div>
          <h3 className="display text-xl mb-1">{sport}</h3>
          <p className="text-sm text-ink-2 mb-3 leading-snug">{SPORT_DESCRIPTOR[sport]}</p>
          <div className="flex items-center justify-between pt-2.5 border-t border-line">
            <span className="text-xs text-ink-2">Browse teams</span>
            <span className="text-terracotta group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </button>
      ))}
    </div>
  </>
);

export default App;
